/**
 * Manufacturer Cart System
 * Handles validation and progress calculation for manufacturer-based container system
 */

class ManufacturerCart {
  constructor() {
    this.cart = null;
    this.manufacturers = new Map();
  }

  /**
   * Fetch current cart from Shopify
   * Prefers window.cartDrawerItemsData or window.cartItemsData when available (they include product metafields from Liquid)
   */
  async fetchCart() {
    try {
      // Prefer cart data with metafields from Liquid (cart-drawer or main-cart)
      if (typeof window.cartDrawerItemsData !== 'undefined' && window.cartDrawerItemsData && window.cartDrawerItemsData.length > 0) {
        this.cart = { items: window.cartDrawerItemsData };
        this._addLineNumbers(this.cart.items);
        return this.cart;
      }
      if (typeof window.cartItemsData !== 'undefined' && window.cartItemsData && window.cartItemsData.length > 0) {
        this.cart = { items: window.cartItemsData };
        this._addLineNumbers(this.cart.items);
        return this.cart;
      }

      const response = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' }
      });
      this.cart = await response.json();
      this._addLineNumbers(this.cart?.items);
      return this.cart;
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }

  _addLineNumbers(items) {
    if (items) {
      items.forEach((item, index) => {
        item.index = index;
        item.line = index + 1;
      });
    }
  }

  /**
   * Fetch product metafields via GraphQL
   */
  async fetchProductMetafields(productIds) {
    // This would require Storefront API access
    // For now, return empty - metafields should be passed from Liquid
    return {};
  }

  /**
   * Group cart items by manufacturer
   */
  groupByManufacturer(cartItems) {
    const groups = new Map();
    const noManufacturer = [];

    for (const item of cartItems) {
      // Try multiple paths for manufacturer data
      const manufacturer = item.product?.metafields?.custom?.manufacturer || 
                          item.manufacturer ||
                          (item.product && item.product.manufacturer);
      
      if (!manufacturer) {
        noManufacturer.push(item);
        continue;
      }

      // Handle both direct value and nested value
      const manufacturerValue = manufacturer.value || manufacturer;
      
      if (!manufacturerValue) {
        noManufacturer.push(item);
        continue;
      }

      // Create a unique manufacturer ID using ID if available, otherwise use name
      // This ensures different manufacturers don't get grouped together
      const manufacturerNombre = manufacturerValue.nombre?.value || 
                                 manufacturerValue.nombre || 
                                 'Unknown Manufacturer';
      
      // Use ID if available and not empty, otherwise use name as fallback
      // This prevents all manufacturers with empty IDs from being grouped as 'unknown'
      let manufacturerId = manufacturerValue.id;
      if (!manufacturerId || manufacturerId === '' || manufacturerId === 'unknown') {
        // Use name as unique identifier if ID is not available
        manufacturerId = `manufacturer_${manufacturerNombre.replace(/\s+/g, '_').toLowerCase()}`;
      }
      
      // Ensure it's a string
      manufacturerId = String(manufacturerId);
      
      if (!groups.has(manufacturerId)) {
        groups.set(manufacturerId, {
          manufacturer: manufacturerValue,
          items: []
        });
      }
      
      groups.get(manufacturerId).items.push(item);
    }

    return { groups, noManufacturer };
  }

  /**
   * Calculate progress for Type 1 (Volume-based) with multiple containers
   * Returns an array of container progress objects
   */
  async calculateType1Progress(manufacturer, items) {
    console.log('[Type1 Volume] Starting calculation', {
      manufacturer: manufacturer.nombre?.value || manufacturer.nombre || 'Unknown',
      itemsCount: items.length
    });

    const width = parseFloat(manufacturer.ancho?.value || 0);
    const depth = parseFloat(manufacturer.profundidad?.value || 0);
    const length = parseFloat(manufacturer.longitud?.value || 0);
    const usefulCapacity = parseFloat(manufacturer.capacidad_util?.value || 0);
    const maxWeight = parseFloat(manufacturer.peso_maximo?.value || 35);
    const minVolumePercent = parseFloat(manufacturer.porcentaje_de_volumen_minimo?.value || 0);
    const maxWeightKg = maxWeight * 1000;

    // Array to hold all containers
    let containers = [];
    let currentContainerIndex = 0;

    // Initialize first container
    let currentContainer = {
      index: currentContainerIndex + 1,
      matrix: [],
      totalWeight: 0,
      cm3Used: 0,
      items: []
    };

    console.log('[Type1 Volume] Container dimensions', {
      width,
      depth,
      length,
      usefulCapacity,
      maxWeight,
      minVolumePercent
    });

    // Helper function to check if container is full
    // Container is full ONLY when volume reaches 100% (not weight)
    // Weight is a constraint but doesn't trigger new container creation
    // Use same rounding logic as finalizeContainer to ensure consistency
    const isContainerFull = (container) => {
      const mUsedCapacity = container.cm3Used / 1000000; // Convert to m³
      let capacityPercent = usefulCapacity > 0 ? (mUsedCapacity / usefulCapacity) * 100 : 0;
      
      // Apply same rounding rules as finalizeContainer
      if (usefulCapacity > 0) {
        const rawPercentage = (mUsedCapacity / usefulCapacity) * 100;
        capacityPercent = Math.min(100, parseFloat(rawPercentage.toFixed(2)));
        if (rawPercentage > 97) {
          capacityPercent = 100;
        }
      }
      
      // Also consider it full if weight exceeded (matches finalizeContainer logic)
      if (container.totalWeight > maxWeightKg) {
        capacityPercent = 100;
      }
      
      const volumeFull = capacityPercent >= 100;
      return volumeFull; // Only check volume, not weight
    };

    // Helper function to finalize a container and create progress object
    const finalizeContainer = (container) => {
      let cm3Used = 0;
      for (const placedItem of container.matrix) {
        const itemVolume = (placedItem.endX - placedItem.startX) * 
                          (placedItem.endY - placedItem.startY) * 
                          (placedItem.endH - placedItem.startH);
        cm3Used += itemVolume;
      }

      const mUsedCapacity = cm3Used / 1000000; // Convert to m³
      let capacityPercent = usefulCapacity > 0 ? (mUsedCapacity / usefulCapacity) * 100 : 0;
      
      // Ensure percentage is within bounds
      capacityPercent = Math.min(100, Math.max(0, capacityPercent));

      // Rounding rules (matching backend logic)
      if (capacityPercent > 95 && capacityPercent <= 100) {
        capacityPercent = 100;
      }

      if (usefulCapacity > 0) {
        const rawPercentage = (mUsedCapacity / usefulCapacity) * 100;
        capacityPercent = Math.min(100, parseFloat(rawPercentage.toFixed(2)));
        if (rawPercentage > 97) {
          capacityPercent = 100;
        }
      }

      // Weight check - if exceeded, force to 100%
      if (container.totalWeight > maxWeightKg) {
        capacityPercent = 100;
      }

      // Validation
      const meetsMinimum = minVolumePercent === 0 || capacityPercent >= minVolumePercent;
      const meetsMaximum = capacityPercent <= 100;
      // Weight must be strictly less than max (not equal to or greater than)
      const meetsWeight = container.totalWeight < maxWeightKg;
      const weightPercent = (container.totalWeight / maxWeightKg) * 100;
      // Show warning when weight is at 70% or more (but less than 100%)
      const weightWarning = weightPercent >= 70 && weightPercent < 100;
      
      // Check if container is invalid: weight reached max but volume hasn't reached minimum
      const isInvalidContainer = container.totalWeight >= maxWeightKg && !meetsMinimum;

      return {
        containerIndex: container.index,
        percentage: capacityPercent,
        current: mUsedCapacity,
        target: usefulCapacity,
        minTarget: (usefulCapacity * minVolumePercent) / 100,
        valid: meetsMinimum && meetsMaximum && meetsWeight,
        totalWeight: container.totalWeight,
        maxWeight: maxWeightKg,
        weightPercent: weightPercent,
        weightWarning: weightWarning,
        isInvalidContainer: isInvalidContainer,
        type: 1,
        items: container.items
      };
    };

    // Prepare all items with their properties
    const itemData = [];
    for (const item of items) {
      const w = parseFloat(item.product?.metafields?.custom?.tama_o_empaquetado_w?.value || 0);
      const l = parseFloat(item.product?.metafields?.custom?.tama_o_empaquetado_l?.value || 0);
      const h = parseFloat(item.product?.metafields?.custom?.tama_o_empaquetado_h?.value || 0);
      const weight = parseFloat(item.product?.metafields?.custom?.pallet_weight?.value || 0);

      // Skip labels (1x1x1)
      if (w === 1 && l === 1 && h === 1) {
        continue;
      }

      // Skip if dimensions are invalid
      if (isNaN(w) || isNaN(l) || isNaN(h) || w <= 0 || l <= 0 || h <= 0) {
        continue;
      }

      const itemVolumeCM3 = w * l * h; // Volume in cm³ per unit
      const weightPerUnit = weight;
      
      itemData.push({
        item: item,
        w: w,
        l: l,
        h: h,
        itemVolumeCM3: itemVolumeCM3,
        weightPerUnit: weightPerUnit,
        remainingQuantity: item.quantity
      });
    }
    
    const minVolumeM3 = (usefulCapacity * minVolumePercent) / 100;
    
    // Helper function to add units to a container
    const addUnitsToContainer = (container, itemInfo, unitsToAdd) => {
      const { item, w, l, h, itemVolumeCM3, weightPerUnit } = itemInfo;
      
      for (let q = 0; q < unitsToAdd; q++) {
        container.matrix.push({
          startX: 0,
          endX: w,
          startY: 0,
          endY: l,
          startH: 0,
          endH: h,
          item: item
        });
        container.cm3Used += itemVolumeCM3;
        container.totalWeight += weightPerUnit;
      }
      
      if (!container.items.find(i => i.product_id === item.product_id)) {
        container.items.push({
          ...item,
          quantity: 0
        });
      }
      const containerItem = container.items.find(i => i.product_id === item.product_id);
      containerItem.quantity = (containerItem.quantity || 0) + unitsToAdd;
    };
    
    // Simple sequential filling: go through items in order and add them to containers
    for (const itemInfo of itemData) {
      while (itemInfo.remainingQuantity > 0) {
        // Check if current container is full, finalize and create new one if needed
        if (isContainerFull(currentContainer) && currentContainer.matrix.length > 0) {
          containers.push(finalizeContainer(currentContainer));
          currentContainerIndex++;
          currentContainer = {
            index: currentContainerIndex + 1,
            matrix: [],
            totalWeight: 0,
            cm3Used: 0,
            items: []
          };
        }
        
        // Calculate how many units can fit in current container
        const remainingVolumeCM3 = (usefulCapacity * 1000000) - currentContainer.cm3Used;
        const remainingWeightKg = maxWeightKg - currentContainer.totalWeight;
        
        const maxUnitsByVolume = Math.floor(remainingVolumeCM3 / itemInfo.itemVolumeCM3);
        const maxUnitsByWeight = itemInfo.weightPerUnit > 0 ? Math.floor(remainingWeightKg / itemInfo.weightPerUnit) : Infinity;
        const maxUnits = Math.min(itemInfo.remainingQuantity, maxUnitsByVolume, maxUnitsByWeight);
        
        if (maxUnits > 0) {
          addUnitsToContainer(currentContainer, itemInfo, maxUnits);
          itemInfo.remainingQuantity -= maxUnits;
        } else {
          // Can't fit any more of this item - move to next item
          break;
        }
      }
    }

    // Finalize the last container only if it has items
    if (currentContainer.matrix.length > 0) {
      containers.push(finalizeContainer(currentContainer));
    }

    // Filter out any empty containers (containers with no items or all items have quantity 0)
    containers = containers.filter(container => 
      container.items && container.items.length > 0 && 
      container.items.some(item => item.quantity > 0)
    );

    // If no containers were created, create an empty one
    if (containers.length === 0) {
      containers.push({
        containerIndex: 1,
        percentage: 0,
        current: 0,
        target: usefulCapacity,
        minTarget: (usefulCapacity * minVolumePercent) / 100,
        valid: minVolumePercent === 0,
        totalWeight: 0,
        maxWeight: maxWeightKg,
        weightPercent: 0,
        weightWarning: false,
        type: 1,
        items: []
      });
    }

    console.log('[Type1 Volume] Final result - multiple containers', {
      containersCount: containers.length,
      containers: containers.map(c => ({
        containerIndex: c.containerIndex,
        percentage: c.percentage,
        current: c.current,
        totalWeight: c.totalWeight,
        valid: c.valid
      }))
    });

    // Return array of containers
    return {
      containers: containers,
      type: 1,
      maxWeight: maxWeightKg,
      // Overall validation: all containers must be valid
      valid: containers.every(c => c.valid)
    };
  }

  /**
   * Calculate total volume from matrix (in cm³)
   */
  calculateMatrixVolume(matrix) {
    let totalVolume = 0;
    for (const placedItem of matrix) {
      const itemVolume = (placedItem.endX - placedItem.startX) * 
                        (placedItem.endY - placedItem.startY) * 
                        (placedItem.endH - placedItem.startH);
      totalVolume += itemVolume;
    }
    return totalVolume;
  }

  /**
   * Place item in 3D space using placement algorithm
   */
  placeItemIn3D(matrix, w, l, h, item, containerWidth, containerDepth, containerLength, unitNumber = 0) {
    // Match original boundary calculations exactly
    // Original: maxXWithSecurityOnCM = (container.width - ((w / 100) / 2)) * 100
    const maxXWithSecurityOnCM = (containerWidth - ((w / 100) / 2)) * 100;
    // Original: maxYWithSecurityOnCM = (container.depth - (container.depth * 0.03)) * 100
    const maxYWithSecurityOnCM = (containerDepth - (containerDepth * 0.03)) * 100;
    // Original: maxHWithSecurityOnCM = (container.length * 100)
    const maxHWithSecurityOnCM = containerLength * 100;

    console.log('[Type1 Volume] placeItemIn3D called', {
      unitNumber,
      itemDimensions: { w, l, h },
      containerDimensions: { containerWidth, containerDepth, containerLength },
      boundaries: {
        maxXWithSecurityOnCM,
        maxYWithSecurityOnCM,
        maxHWithSecurityOnCM
      },
      currentMatrixSize: matrix.length
    });

    // Determine orientation (containers are wider than long) - match original logic
    let maxY = l;
    let maxX = w;
    let maxH = h;

    // Original: determine the longest side (containers are always more width than length)
    if (maxY > maxX) {
      const oldY = maxY;
      const oldX = maxX;
      maxX = oldY;
      maxY = oldX;
      console.log('[Type1 Volume] Orientation swapped', { original: { w, l, h }, swapped: { maxX, maxY, maxH } });
    }

    // Control variable to determine if the object was successfully placed (match original)
    let itemWasPlaced = false;
    
    // Initialize search statistics (needed for logging even if matrix is empty)
    let positionsChecked = 0;
    let positionsWithConflicts = 0;
    let positionsThatDontFit = 0;

    // If matrix is empty, place at origin (match original)
    if (matrix.length === 0) {
      matrix.push({ startX: 0, endX: maxX, startY: 0, endY: maxY, startH: 0, endH: maxH, item: item });
      itemWasPlaced = true;
      console.log('[Type1 Volume] Placed first item at origin');
      return itemWasPlaced;
    } else {
      // Search for free space in 5cm increments - match original search pattern
      // Original searches up to maxXWithSecurityOnCM directly, not maxXWithSecurityOnCM - maxX
      
      hLoop: for (let h = 0; h < maxHWithSecurityOnCM; h += 5) {
        for (let y = 0; y < maxYWithSecurityOnCM; y += 5) {
          for (let x = 0; x < maxXWithSecurityOnCM; x += 5) {
          positionsChecked++;
          
          // Original collision detection: check if starting point is inside existing item
          let compromisedCoords = false;
          for (const mItem of matrix) {
            // Original: if ((x >= mItem.startX && x <= mItem.endX) && (y >= mItem.startY && y <= mItem.endY) && (h >= mItem.startH && h <= mItem.endH))
            if ((x >= mItem.startX && x <= mItem.endX) && (y >= mItem.startY && y <= mItem.endY) && (h >= mItem.startH && h <= mItem.endH)) {
              compromisedCoords = true;
              positionsWithConflicts++;
              break;
            }
          }

          // If coordinates aren't compromised, check if item fits
          if (!compromisedCoords) {
            const newX = x + maxX;
            const newY = y + maxY;
            const newH = h + maxH;

            // Original boundary check: only checks X and Y, not H
            // Original: if (newX < maxXWithSecurityOnCM && newY < maxYWithSecurityOnCM)
            if (newX < maxXWithSecurityOnCM && newY < maxYWithSecurityOnCM) {
              // Match original exactly - no additional overlap check
              // The original only checks if starting point is compromised, not if entire bounding box overlaps
              const placedItem = {
                startX: x,
                endX: newX,
                startY: y,
                endY: newY,
                startH: h,
                endH: newH,
                item: item
              };
              matrix.push(placedItem);
              itemWasPlaced = true;
              console.log('[Type1 Volume] Item placed successfully', {
                unitNumber,
                position: { x, y, h },
                dimensions: { maxX, maxY, maxH },
                placedItem,
                positionsChecked,
                positionsWithConflicts,
                positionsThatDontFit
              });
              break hLoop;
            } else {
              positionsThatDontFit++;
            }
          }
        }
      }
      }
    }

    // Log failure if item wasn't placed
    if (!itemWasPlaced) {
      console.warn('[Type1 Volume] Item placement FAILED - no space found', {
        unitNumber,
        itemDimensions: { w, l, h, maxX, maxY, maxH },
        containerBoundaries: {
          maxXWithSecurityOnCM,
          maxYWithSecurityOnCM,
          maxHWithSecurityOnCM
        },
        searchStats: {
          positionsChecked,
          positionsWithConflicts,
          positionsThatDontFit,
          positionsAvailable: positionsChecked - positionsWithConflicts - positionsThatDontFit
        },
        currentMatrixSize: matrix.length,
        matrixItems: matrix.map(m => ({
          bounds: { 
            x: [m.startX, m.endX], 
            y: [m.startY, m.endY], 
            h: [m.startH, m.endH] 
          },
          volume: (m.endX - m.startX) * (m.endY - m.startY) * (m.endH - m.startH) / 1000000 // Convert to m³
        }))
      });
    }

    // Return (match original)
    return itemWasPlaced;
  }

  /**
   * Calculate progress for Type 2 (Min price)
   */
  calculateType2Progress(manufacturer, items) {
    try {
      // precio_minimo is stored as a number in euros, convert to cents for comparison
      // Handle different possible structures for manufacturer metaobject
      const precioMinimoValue = manufacturer?.precio_minimo?.value || 
                               manufacturer?.precio_minimo ||
                               0;
      const minPriceEuros = parseFloat(precioMinimoValue);
      const minPrice = minPriceEuros * 100; // Convert euros to cents
      let totalPrice = 0;

      for (const item of items) {
        totalPrice += item.final_line_price || item.line_price || 0;
      }

      const percentage = minPrice > 0 ? (totalPrice / minPrice) * 100 : 100;
      const valid = totalPrice >= minPrice;

      return {
        percentage: Math.min(100, percentage),
        current: totalPrice,
        target: minPrice,
        valid: valid,
        type: 2
      };
    } catch (error) {
      console.error('[Type2] Error calculating progress:', error, {
        manufacturer,
        itemsCount: items?.length
      });
      // Return a safe default
      return {
        percentage: 0,
        current: 0,
        target: 0,
        valid: false,
        type: 2
      };
    }
  }

  /**
   * Calculate progress for Type 3 (Min units per product)
   */
  calculateType3Progress(manufacturer, items) {
    const productProgress = [];
    let allValid = true;
    let totalPercentage = 0;
    let productsWithMinUnits = 0;

    for (const item of items) {
      const minUnits = parseFloat(item.product?.metafields?.custom?.min_units?.value || 0);
      const quantity = item.quantity || 0;
      const valid = minUnits === 0 || quantity >= minUnits;

      if (!valid) {
        allValid = false;
      }

      // Calculate percentage for this product
      let productPercentage = 0;
      if (minUnits > 0) {
        productPercentage = Math.min(100, (quantity / minUnits) * 100);
        productsWithMinUnits++;
        totalPercentage += productPercentage;
      } else {
        // Products without min_units are considered 100% complete
        productPercentage = 100;
        productsWithMinUnits++;
        totalPercentage += 100;
      }

      productProgress.push({
        productId: item.product_id,
        productTitle: item.product_title || item.title,
        quantity: quantity,
        minUnits: minUnits,
        valid: valid,
        percentage: productPercentage
      });
    }

    // Calculate overall percentage as average of all product percentages
    const overallPercentage = productsWithMinUnits > 0 
      ? totalPercentage / productsWithMinUnits 
      : (allValid ? 100 : 0);

    return {
      percentage: Math.min(100, Math.round(overallPercentage)),
      current: items.length,
      target: items.length,
      valid: allValid,
      productProgress: productProgress,
      type: 3
    };
  }

  /**
   * Calculate progress for Type 4 (Min price + Min units)
   */
  calculateType4Progress(manufacturer, items) {
    const type2Progress = this.calculateType2Progress(manufacturer, items);
    const type3Progress = this.calculateType3Progress(manufacturer, items);

    const valid = type2Progress.valid && type3Progress.valid;
    const percentage = valid ? 100 : Math.min(type2Progress.percentage, type3Progress.percentage);

    return {
      percentage: percentage,
      current: {
        price: type2Progress.current,
        products: type3Progress.current
      },
      target: {
        price: type2Progress.target,
        products: type3Progress.target
      },
      valid: valid,
      priceProgress: type2Progress,
      unitsProgress: type3Progress,
      type: 4
    };
  }

  /**
   * Calculate progress for all manufacturers in cart
   */
  async calculateProgress(cartItems) {
    const { groups } = this.groupByManufacturer(cartItems);
    const progress = {};

    for (const [manufacturerId, group] of groups) {
      const manufacturer = group.manufacturer;
      const items = group.items;
      const tipo = parseInt(manufacturer.tipo?.value || 0);

      let manufacturerProgress;

      switch (tipo) {
        case 1:
          manufacturerProgress = await this.calculateType1Progress(manufacturer, items);
          break;
        case 2:
          manufacturerProgress = this.calculateType2Progress(manufacturer, items);
          break;
        case 3:
          manufacturerProgress = this.calculateType3Progress(manufacturer, items);
          break;
        case 4:
          manufacturerProgress = this.calculateType4Progress(manufacturer, items);
          break;
        default:
          manufacturerProgress = { valid: true, percentage: 100, type: 0 };
      }

      progress[manufacturerId] = {
        manufacturer: manufacturer,
        progress: manufacturerProgress,
        items: items
      };
    }

    return progress;
  }

  /**
   * Check if checkout is allowed (all manufacturers must be valid)
   */
  async isCheckoutAllowed(cartItems) {
    const progress = await this.calculateProgress(cartItems);
    const invalidManufacturers = [];

    for (const [manufacturerId, data] of Object.entries(progress)) {
      // Handle Type 1 with multiple containers - check if all containers are valid
      if (data.progress.type === 1 && data.progress.containers) {
        // Check for containers that are invalid (weight >= max or other validation failures)
        const invalidContainers = data.progress.containers.filter(c => !c.valid);
        if (invalidContainers.length > 0) {
          invalidManufacturers.push({
            id: manufacturerId,
            name: data.manufacturer.nombre?.value || data.manufacturer.nombre || 'Unknown',
            progress: data.progress,
            invalidContainers: invalidContainers.map(c => c.containerIndex)
          });
        }
      } else if (!data.progress.valid) {
        invalidManufacturers.push({
          id: manufacturerId,
          name: data.manufacturer.nombre?.value || data.manufacturer.nombre || 'Unknown',
          progress: data.progress
        });
      }
    }

    return {
      allowed: invalidManufacturers.length === 0,
      invalidManufacturers: invalidManufacturers
    };
  }

  /**
   * Check if adding an item would exceed weight before reaching minimum volume
   * Returns { canAdd: boolean, reason: string }
   */
  async checkWeightBeforeMinVolume(manufacturer, items, itemToAdd, quantityToAdd) {
    const maxWeight = parseFloat(manufacturer.peso_maximo?.value || 35) * 1000;
    const usefulCapacity = parseFloat(manufacturer.capacidad_util?.value || 0);
    const minVolumePercent = parseFloat(manufacturer.porcentaje_de_volumen_minimo?.value || 0);
    const minVolumeM3 = (usefulCapacity * minVolumePercent) / 100;
    
    // Get item dimensions and weight
    const w = parseFloat(itemToAdd.product?.metafields?.custom?.tama_o_empaquetado_w?.value || 0);
    const l = parseFloat(itemToAdd.product?.metafields?.custom?.tama_o_empaquetado_l?.value || 0);
    const h = parseFloat(itemToAdd.product?.metafields?.custom?.tama_o_empaquetado_h?.value || 0);
    const weightPerUnit = parseFloat(itemToAdd.product?.metafields?.custom?.pallet_weight?.value || 0);
    
    if (w === 1 && l === 1 && h === 1) {
      // Labels - always allow
      return { canAdd: true, reason: '' };
    }
    
    if (isNaN(w) || isNaN(l) || isNaN(h) || w <= 0 || l <= 0 || h <= 0) {
      // Invalid dimensions - allow (will be validated elsewhere)
      return { canAdd: true, reason: '' };
    }
    
    const itemVolumeCM3 = w * l * h;
    const totalWeightToAdd = weightPerUnit * quantityToAdd;
    
    // Calculate current state
    const progress = await this.calculateType1Progress(manufacturer, items);
    
    if (!progress.containers || progress.containers.length === 0) {
      // No containers yet - check if adding would exceed weight
      const newWeight = totalWeightToAdd;
      if (newWeight >= maxWeight) {
        return { 
          canAdd: false, 
          reason: this.translate('weight_limit_exceeded') 
        };
      }
      return { canAdd: true, reason: '' };
    }
    
    // Check the last container (the one being filled)
    const lastContainer = progress.containers[progress.containers.length - 1];
    const currentWeight = lastContainer.totalWeight || 0;
    const currentVolumeM3 = lastContainer.current || 0;
    const newWeight = currentWeight + totalWeightToAdd;
    const newVolumeM3 = currentVolumeM3 + (itemVolumeCM3 * quantityToAdd / 1000000);
    
    // Check if container is already at max weight
    if (currentWeight >= maxWeight) {
      // Container is at max weight - check if we can create new container
      // Only allow if current container has reached min volume
      if (currentVolumeM3 >= minVolumeM3) {
        // Can create new container
        return { canAdd: true, reason: '' };
      } else {
        // Can't create new container yet - disable add
        return { 
          canAdd: false, 
          reason: this.translate('weight_limit_reached_before_min_volume') 
        };
      }
    }
    
    // Check if adding would exceed or reach max weight (must stay strictly under)
    // We need: newWeight < maxWeight, so if newWeight >= maxWeight, it's not allowed
    if (newWeight >= maxWeight) {
      // Adding would reach or exceed weight - check if we've reached min volume
      if (currentVolumeM3 >= minVolumeM3) {
        // Has reached min volume - can create new container
        // But we still need to check if the new weight would fit in a new container
        // For now, allow it - the new container will handle the weight
        return { canAdd: true, reason: '' };
      } else {
        // Would exceed weight before reaching min volume - disable add
        return { 
          canAdd: false, 
          reason: this.translate('weight_would_exceed_before_min_volume') 
        };
      }
    }
    
    // Also check if current weight is already at or over max (shouldn't happen, but check)
    if (currentWeight >= maxWeight) {
      // Already at max weight - only allow if min volume reached
      if (currentVolumeM3 >= minVolumeM3) {
        return { canAdd: true, reason: '' };
      } else {
        return { 
          canAdd: false, 
          reason: this.translate('weight_limit_reached_before_min_volume') 
        };
      }
    }
    
    // Can add without issues
    return { canAdd: true, reason: '' };
  }

  /**
   * Validate if adding a product to cart would violate manufacturer limits
   * 
   * For Type 1 (volume-based), we check weight limits before allowing addition.
   */
  async validateAddToCart(productId, quantity, variantId) {
    const cart = await this.fetchCart();
    if (!cart || !cart.items || cart.items.length === 0) {
      // Empty cart - allow adding first item
      return { valid: true, message: '' };
    }

    // Create temporary cart with new item
    const tempItems = [...cart.items];
    
    // Check if product already in cart
    const existingItemIndex = tempItems.findIndex(
      item => item.product_id === productId && item.variant_id === variantId
    );

    if (existingItemIndex >= 0) {
      // Product already in cart - we can validate with existing metafields
      const itemToAdd = { ...tempItems[existingItemIndex] };
      tempItems[existingItemIndex].quantity += quantity;
      
      // For Type 1, check weight limits and min volume requirements
      const { groups } = this.groupByManufacturer(tempItems);
      for (const [manufacturerId, group] of groups) {
        const tipo = parseInt(group.manufacturer.tipo?.value || 0);
        if (tipo === 1) {
          // Type 1: Check weight limits before min volume
          // Get items without the quantity we're about to add
          const itemsBeforeAdd = group.items.map(item => {
            if (item.product_id === productId && item.variant_id === variantId) {
              return { ...item, quantity: item.quantity - quantity };
            }
            return item;
          }).filter(item => item.quantity > 0);
          
          const weightCheck = await this.checkWeightBeforeMinVolume(
            group.manufacturer, 
            itemsBeforeAdd,
            itemToAdd,
            quantity
          );
          
          if (!weightCheck.canAdd) {
            return {
              valid: false,
              message: weightCheck.reason
            };
          }
          
          // Also check if any container exceeds max weight (shouldn't happen, but double-check)
          const maxWeight = parseFloat(group.manufacturer.peso_maximo?.value || 35) * 1000;
          const progress = await this.calculateType1Progress(group.manufacturer, group.items);
          
          if (progress.containers && Array.isArray(progress.containers)) {
            for (const container of progress.containers) {
              if (container.totalWeight >= maxWeight) {
                return {
                  valid: false,
                  message: this.translate('weight_limit_exceeded')
                };
              }
            }
          }
        }
      }
      
      // Check checkout status for other validation issues
      const checkoutStatus = await this.isCheckoutAllowed(tempItems);
      
      if (!checkoutStatus.allowed) {
        // Check if it's a Type 1 manufacturer - if so, we already checked weight above
        const { groups } = this.groupByManufacturer(tempItems);
        for (const [manufacturerId, group] of groups) {
          const tipo = parseInt(group.manufacturer.tipo?.value || 0);
          if (tipo === 1) {
            // Type 1: Weight already checked, allow and validate after add
            console.log('[Type1 Validation] Allowing add - will validate after cart update');
            return { valid: true, message: '' };
          }
        }
        
        // For other types, validate normally
        const firstInvalid = checkoutStatus.invalidManufacturers[0];
        return {
          valid: false,
          message: `Cannot add product: ${firstInvalid.name} requirements not met.`
        };
      }
    } else {
      // New product - check if cart has Type 1 manufacturers
      const { groups } = this.groupByManufacturer(tempItems);
      let hasType1 = false;
      for (const [manufacturerId, group] of groups) {
        const tipo = parseInt(group.manufacturer.tipo?.value || 0);
        if (tipo === 1) {
          hasType1 = true;
          break;
        }
      }
      
      if (hasType1) {
        // Type 1 in cart - we can't fully validate new product without its metafields
        // Allow adding and validate after (matches backend)
        console.log('[Type1 Validation] Allowing new product add - will validate after cart update');
        return { valid: true, message: '' };
      }
      
      // For non-Type 1, validate existing cart state
      const checkoutStatus = await this.isCheckoutAllowed(tempItems);
      if (!checkoutStatus.allowed) {
        const firstInvalid = checkoutStatus.invalidManufacturers[0];
        return {
          valid: false,
          message: `Cannot add product: ${firstInvalid.name} requirements not met.`
        };
      }
    }

    return { valid: true, message: '' };
  }

  /**
   * Validate if updating quantity would violate manufacturer limits
   * Returns { valid: boolean, message: string }
   */
  async validateQuantityUpdate(line, newQuantity) {
    // Allow removal (quantity 0) - no validation needed
    if (newQuantity === 0) {
      return { valid: true, message: '' };
    }
    
    // Validate that line number and quantity are valid
    if (!line || newQuantity < 1) {
      return { valid: false, message: 'Invalid quantity' };
    }

    const cart = await this.fetchCart();
    if (!cart || !cart.items || cart.items.length === 0) {
      return { valid: true, message: '' };
    }

    // Find the item by line number (1-based)
    const itemIndex = cart.items.findIndex(item => item.line === line || item.index === (line - 1));
    if (itemIndex < 0) {
      return { valid: false, message: 'Item not found in cart' };
    }

    const item = cart.items[itemIndex];
    const currentQuantity = item.quantity;
    const quantityChange = newQuantity - currentQuantity;

    // If decreasing quantity, always allow
    if (quantityChange <= 0) {
      return { valid: true, message: '' };
    }

    // Create temporary cart with updated quantity
    const tempItems = [...cart.items];
    tempItems[itemIndex] = { ...item, quantity: newQuantity };

    // Check if this item belongs to a Type 1 manufacturer
    const manufacturer = item.product?.metafields?.custom?.manufacturer?.value;
    if (manufacturer) {
      const tipo = parseInt(manufacturer.tipo?.value || 0);
      if (tipo === 1) {
        // Type 1: Check weight limits before min volume using the same logic as add to cart
        // Get items without the quantity we're about to add
        const itemsBeforeAdd = cart.items.map(cartItem => {
          if (cartItem.product_id === item.product_id && cartItem.variant_id === item.variant_id) {
            return { ...cartItem, quantity: cartItem.quantity - quantityChange };
          }
          return cartItem;
        }).filter(cartItem => cartItem.quantity > 0);

        const weightCheck = await this.checkWeightBeforeMinVolume(
          manufacturer,
          itemsBeforeAdd,
          item,
          quantityChange
        );

        if (!weightCheck.canAdd) {
          return {
            valid: false,
            message: weightCheck.reason
          };
        }

        // Also check if any container exceeds max weight (double-check after adding)
        const maxWeight = parseFloat(manufacturer.peso_maximo?.value || 35) * 1000;
        const { groups } = this.groupByManufacturer(tempItems);

        // Find the group for this manufacturer
        for (const [manufacturerId, group] of groups) {
          if (manufacturerId === manufacturer.id) {
            const progress = await this.calculateType1Progress(group.manufacturer, group.items);

            // Check all containers for weight violations
            if (progress.containers && Array.isArray(progress.containers)) {
              for (const container of progress.containers) {
                if (container.totalWeight >= maxWeight) {
                  return {
                    valid: false,
                    message: this.translate('quantity_update_exceeds_weight')
                  };
                }
              }
            }
            break;
          }
        }

        // Type 1 passed all weight checks — allow the update.
        // Do NOT run the general isCheckoutAllowed check for Type 1 because:
        // adding items that overflow a container creates a new container, which starts
        // at 0 % and would incorrectly fail the minimum-volume check.
        return { valid: true, message: '' };
      }
    }

    // Check general checkout status (non-Type-1 manufacturers only)
    const checkoutStatus = await this.isCheckoutAllowed(tempItems);
    if (!checkoutStatus.allowed) {
      const firstInvalid = checkoutStatus.invalidManufacturers[0];
      return {
        valid: false,
        message: `Cannot update quantity: ${firstInvalid.name} requirements not met.`
      };
    }

    return { valid: true, message: '' };
  }

  /**
   * Translate a key using theme.manufacturerCart translations
   */
  translate(key, params = {}) {
    // Access translations from theme object
    if (typeof theme !== 'undefined' && theme.manufacturerCart) {
      let translation = theme.manufacturerCart[key] || '';
      
      // Handle variable substitution (e.g., {{ amount }})
      if (params && Object.keys(params).length > 0) {
        Object.keys(params).forEach(param => {
          translation = translation.replace(new RegExp(`{{\\s*${param}\\s*}}`, 'g'), params[param]);
        });
      }
      
      return translation || key;
    }
    
    // Final fallback
    return key;
  }
}

// Export for use in other files
window.ManufacturerCart = ManufacturerCart;

